import React, { useState, useEffect, useRef } from 'react';
import './PipelineAnimation.css';

const DOC = {
  type: 'Supporting Record',
  tagColor: '#60a5fa',
  filename: 'supporting_rec_0092.pdf',
  autoFields: [
    { label: 'Full Name',     value: 'J. Williams' },
    { label: 'Category',      value: 'Assessment Report' },
    { label: 'Review Date',   value: 'Jan 28, 2026' },
    { label: 'Status',        value: 'Active' },
  ],
  reviewField: { label: 'Effective Date', model: 'Undetermined', annotated: 'Mar 1, 2026', confidence: 63 },
};

const PipelineAnimation = () => {
  const [cycleKey, setCycleKey]           = useState(0);
  const [phase, setPhase]                 = useState('scanning');
  const [visibleFields, setVisibleFields] = useState(0);
  const timeoutsRef = useRef([]);

  const clear = () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };
  const after = (fn, ms) => { const id = setTimeout(fn, ms); timeoutsRef.current.push(id); };

  useEffect(() => {
    clear();
    setPhase('scanning');
    setVisibleFields(0);

    let t = 0;

    const SCAN      = 1000;
    const CLASSIFY  = 2200;
    const FIELD     = 700;
    const HOLD      = 1800;
    const ANNOT     = 4000;
    const QA_MS     = 3500;
    const CONF_HOLD = 900;
    const REPORT    = 6000;

    t += SCAN;
    after(() => setPhase('classified'), t);

    t += CLASSIFY;
    after(() => {
      setPhase('extracting');
      DOC.autoFields.forEach((_, i) => {
        after(() => setVisibleFields(i + 1), i * FIELD);
      });
      after(() => {
        setVisibleFields(DOC.autoFields.length + 1);
        after(() => setPhase('complete'), FIELD);
      }, DOC.autoFields.length * FIELD);
    }, t);

    t += (DOC.autoFields.length + 1) * FIELD + HOLD;
    after(() => setPhase('annotating'), t);

    t += ANNOT;
    after(() => setPhase('qa'), t);

    t += QA_MS;
    after(() => setPhase('confirmed'), t);

    t += CONF_HOLD;
    after(() => setPhase('reporting'), t);

    t += REPORT;
    after(() => {
      setPhase('out');
      after(() => setCycleKey(k => k + 1), 600);
    }, t);

    return clear;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  const isClassified = phase !== 'scanning';
  const isExtracting = phase === 'extracting';
  const isComplete   = ['complete','annotating','qa','confirmed','reporting'].includes(phase);
  const isAnnotating = ['annotating','qa','confirmed','reporting'].includes(phase);
  const isQA         = ['qa','confirmed','reporting'].includes(phase);
  const isConfirmed  = ['confirmed','reporting'].includes(phase);
  const isReporting  = phase === 'reporting';
  const isOut        = phase === 'out';

  const docLeft =
    isReporting || isOut         ? '90%' :
    isComplete  || isExtracting  ? '32%' :
    isClassified                 ? '10%' : '-10%';

  // SVG viewBox "0 0 600 390"
  // Main lane: node top=164px, center y=196, bottom y=228
  // Fork lane: node top=292px, center y=324, bottom y=356
  // Node half-width = 32 (64px nodes)
  // x positions: Classify=60, Extract=192, Annotator=312, QA=420, Report=540 (in 600-wide viewBox)
  const C = {
    clEx:  isClassified ? '#4ade80' : '#e5e7eb',
    auto:  isConfirmed  ? '#4ade80' : '#e5e7eb',
    fork:  isAnnotating ? '#fbbf24' : '#e5e7eb',
    aq:    isQA         ? '#fbbf24' : '#e5e7eb',
    merge: isConfirmed  ? '#4ade80' : '#e5e7eb',
  };

  const ns = (done, active) => done ? 'done' : active ? 'active' : 'idle';
  const nState = {
    classify: ns(isClassified, phase === 'scanning'),
    extract:  ns(isComplete, isExtracting),
    annotate: ns(isQA, phase === 'annotating'),
    qa:       ns(isConfirmed, phase === 'qa'),
    report:   ns(false, isReporting),
  };

  const panel =
    isReporting                         ? 'report'   :
    phase === 'qa' || isConfirmed       ? 'qa'       :
    phase === 'annotating'              ? 'annotate' :
    isComplete || isExtracting          ? 'extract'  :
    isClassified                        ? 'classify' : 'idle';

  const NodeBox = ({ state, color, num, icon }) => {
    const isDone   = state === 'done';
    const isActive = state === 'active';
    return (
      <div className="pa-nb"
        style={
          isActive ? { borderColor: color, background: `${color}10`, boxShadow: `0 6px 0 ${color}66, 0 0 0 4px ${color}15` } :
          isDone   ? { borderColor: '#4ade8055', boxShadow: '0 6px 0 #4ade8040' } : {}
        }>
        {isDone ? (
          <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
            <path d="M2.5 7l3.5 3.5 5.5-6" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : icon ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"
            style={{ color: isActive ? color : '#d1d5db', transition: 'color 0.3s' }}>
            {icon}
          </svg>
        ) : (
          <span className="pa-nb-num" style={{ color: isActive ? color : '#d1d5db' }}>{num}</span>
        )}
      </div>
    );
  };

  return (
    <div className="pa-wrap">
      <div className={`pa-card${isOut ? ' pa-card--out' : ''}`}>

        {/* ── Unified scene ── */}
        <div className="pa-scene">

          {/* Content panels — always at top of scene, never translated */}
          <div className={`pa-panel${panel === 'classify' ? ' pa-panel--on' : ''}`}>
            <div className="pa-cls-body">
              <div className="pa-cls-file-row">
                <svg className="pa-file-ic" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span className="pa-fname">{DOC.filename}</span>
              </div>
              <div className="pa-cls-badge-wrap">
                <div className="pa-cls-badge" style={{ borderColor: `${DOC.tagColor}35`, background: `${DOC.tagColor}0c` }}>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke={DOC.tagColor} strokeWidth="1.5" opacity="0.5"/>
                    <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke={DOC.tagColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="pa-cls-type" style={{ color: DOC.tagColor }}>{DOC.type}</span>
                  <span className="pa-cls-conf">99.2%</span>
                </div>
              </div>
            </div>
          </div>

          <div className={`pa-panel${panel === 'extract' ? ' pa-panel--on' : ''}`}>
            <div className="pa-ext-body">
              <div className="pa-ext-header">
                <span className="pa-ext-title">Extracted Fields</span>
                <span className="pa-ext-tag" style={{ color: DOC.tagColor, background: `${DOC.tagColor}12`, borderColor: `${DOC.tagColor}40` }}>
                  {DOC.type}
                </span>
              </div>
              <div className="pa-ext-split">
                {/* Left: high-confidence auto fields */}
                <div className="pa-fields-col">
                  {DOC.autoFields.slice(0, Math.min(visibleFields, DOC.autoFields.length)).map((f, i) => (
                    <div key={i} className="pa-field-row" style={{ animationDelay: `${i * 0.06}s` }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <circle cx="6" cy="6" r="5.5" stroke="#4ade80" strokeWidth="1.2"/>
                        <path d="M3 6l2 2 4-4" stroke="#4ade80" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="pa-field-lbl">{f.label}</span>
                      <span className="pa-field-val">{f.value}</span>
                    </div>
                  ))}
                </div>
                {/* Right: undetermined / review field */}
                {visibleFields > DOC.autoFields.length && (
                  <div className="pa-fields-col pa-fields-col--review">
                    <div className="pa-review-card" style={{
                      borderColor: isConfirmed ? '#4ade8040' : '#fbbf2440',
                      background: isConfirmed ? '#f0fdf4' : '#fffbeb',
                    }}>
                      <div className="pa-review-card-lbl" style={{ color: isConfirmed ? '#059669' : '#d97706' }}>
                        {isConfirmed ? 'Verified' : 'For Review'}
                      </div>
                      <div className="pa-review-card-field">{DOC.reviewField.label}</div>
                      <div className="pa-review-card-val" style={{ color: isConfirmed ? '#4ade80' : '#d97706' }}>
                        {isConfirmed ? DOC.reviewField.annotated : DOC.reviewField.model}
                      </div>
                      {!isConfirmed && (
                        <div className="pa-conf-track">
                          <div className="pa-conf-fill" style={{ width: `${DOC.reviewField.confidence}%` }}/>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`pa-panel${panel === 'annotate' ? ' pa-panel--on' : ''}`}>
            <div className="pa-ann-body">
              <div className="pa-ann-header">
                <span className="pa-role-pill pa-role-pill--amber">Human Annotation</span>
                <span className="pa-ann-field">{DOC.reviewField.label}</span>
              </div>
              <div className="pa-ann-row">
                <div className="pa-ann-col">
                  <div className="pa-ann-lbl">Model extracted</div>
                  <div className="pa-ann-val" style={{ color: '#d97706' }}>"{DOC.reviewField.model}"</div>
                  <div className="pa-conf-track">
                    <div className="pa-conf-fill" style={{ width: `${DOC.reviewField.confidence}%` }}/>
                  </div>
                  <div className="pa-conf-label">{DOC.reviewField.confidence}% — below threshold</div>
                </div>
                <div className="pa-ann-divider">→</div>
                <div className="pa-ann-col">
                  <div className="pa-ann-lbl">Annotator reviewing</div>
                  <div className="pa-av-row">
                    <div className="pa-av pa-av--amber">A</div>
                    <div>
                      <div className="pa-av-name">Reviewing field…</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`pa-panel${panel === 'qa' ? ' pa-panel--on' : ''}`}>
            <div className="pa-ann-body">
              <div className="pa-ann-header">
                <span className="pa-role-pill pa-role-pill--purple">QA Review</span>
                <span className="pa-ann-field">{DOC.reviewField.label}</span>
              </div>
              <div className="pa-ann-row">
                <div className="pa-ann-col">
                  <div className="pa-ann-lbl">Annotator submitted</div>
                  <div className="pa-ann-val" style={{ color: '#374151', fontStyle: 'italic' }}>
                    "{DOC.reviewField.annotated}"
                  </div>
                </div>
                <div className="pa-ann-divider">→</div>
                <div className="pa-ann-col">
                  <div className="pa-ann-lbl">QA Reviewer</div>
                  <div className="pa-av-row">
                    <div className="pa-av pa-av--purple">Q</div>
                    <div>
                      <div className="pa-av-name" style={{ color: isConfirmed ? '#4ade80' : '#7c3aed' }}>
                        {isConfirmed ? '✓ Passed' : 'Checking accuracy…'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`pa-panel${panel === 'report' ? ' pa-panel--on' : ''}`}>
            <div className="pa-rep-body">
              <div className="pa-rep-header">
                <div className="pa-rep-icon">
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
                    <rect x="2" y="3" width="16" height="15" rx="2" stroke="#d8572a" strokeWidth="1.5"/>
                    <path d="M6 3V1.5M14 3V1.5" stroke="#d8572a" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="2" y1="7" x2="18" y2="7" stroke="#d8572a" strokeWidth="1.2"/>
                    <rect x="4.5" y="9.5" width="5" height="3.5" rx="0.5" fill="#d8572a" opacity="0.5"/>
                    <rect x="11" y="9.5" width="5" height="1.5" rx="0.5" fill="#d8572a" opacity="0.25"/>
                    <rect x="11" y="12" width="3.5" height="1.5" rx="0.5" fill="#d8572a" opacity="0.25"/>
                  </svg>
                </div>
                <div>
                  <div className="pa-rep-title">Monthly Report</div>
                  <div className="pa-rep-client">Jan 2026 · Next delivery Feb 1, 2026</div>
                </div>
              </div>

              <div className="pa-rep-metrics">
                <div className="pa-rep-metric">
                  <div className="pa-rep-metric-val" style={{ color: '#d8572a' }}>98.4%</div>
                  <div className="pa-rep-metric-lbl">Field Accuracy</div>
                </div>
                <div className="pa-rep-metric-div"/>
                <div className="pa-rep-metric">
                  <div className="pa-rep-metric-val">83%</div>
                  <div className="pa-rep-metric-lbl">Automation Rate</div>
                </div>
                <div className="pa-rep-metric-div"/>
                <div className="pa-rep-metric">
                  <div className="pa-rep-metric-val">1,247</div>
                  <div className="pa-rep-metric-lbl">Docs Processed</div>
                </div>
                <div className="pa-rep-metric-div"/>
                <div className="pa-rep-metric">
                  <div className="pa-rep-metric-val">99.1%</div>
                  <div className="pa-rep-metric-lbl">Annotator Agree.</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Pipeline group — slides down when classify begins ── */}
          <div className={`pa-pipeline${isClassified ? '' : ' pa-pipeline--scan'}`}>

            {/* SVG connection paths */}
            {/* viewBox 0 0 600 440 | main lane center y=238 | fork lane center y=372 | node half=32 */}
            {/* x positions: Classify=60, Extract=192, Annotator=312, QA=420, Report=540         */}
            <svg className="pa-svg" viewBox="0 0 600 390" preserveAspectRatio="none" width="100%" height="100%">

              {/* Classify → Extract */}
              <line x1="92" y1="196" x2="160" y2="196"
                stroke={C.clEx} strokeWidth="2.5" style={{ transition: 'stroke 0.5s' }}/>

              {/* Extract → Report (auto lane) */}
              <line x1="224" y1="196" x2="508" y2="196"
                stroke={C.auto} strokeWidth="2.5"
                strokeDasharray={isConfirmed || !isComplete ? 'none' : '8 5'}
                style={{ transition: 'stroke 0.5s' }}/>

              {/* Auto label */}
              {isComplete && (
                <text x="366" y="186" textAnchor="middle"
                  fontSize="9.5" fontFamily="system-ui" fontWeight="600"
                  fill={isConfirmed ? '#4ade80' : '#9ca3af'}
                  style={{ transition: 'fill 0.4s' }}>
                  {DOC.autoFields.length} auto-verified
                </text>
              )}

              {/* Fork: Extract bottom → Annotator left */}
              <path d="M 192,228 C 192,324 280,324 280,324"
                stroke={C.fork} strokeWidth="2.5" fill="none"
                style={{ transition: 'stroke 0.5s' }}/>

              {/* Annotator → QA */}
              <line x1="344" y1="324" x2="388" y2="324"
                stroke={C.aq} strokeWidth="2.5"
                style={{ transition: 'stroke 0.5s' }}/>

              {/* QA → Report merge */}
              <path d="M 452,324 C 508,324 508,228 508,196"
                stroke={C.merge} strokeWidth="2.5" fill="none"
                style={{ transition: 'stroke 0.5s' }}/>

            </svg>

            {/* Traveling document */}
            <div className="pa-doc"
              style={{ left: docLeft, opacity: phase === 'scanning' || isOut ? 0 : 1 }}>
              <div className="pa-doc-fold"/>
              {[72, 45, 82, 38].map((w, i) => (
                <div key={i} className="pa-doc-line" style={{ width: `${w}%` }}/>
              ))}
            </div>

            {/* CLASSIFY node */}
            <div className="pa-node" style={{ left: '10%', top: '164px' }}>
              {nState.classify === 'active' && <div className="pa-ring" style={{ borderColor: DOC.tagColor }}/>}
              <NodeBox state={nState.classify} color={DOC.tagColor} num="1"/>
              <span className="pa-nlbl" style={{
                color: nState.classify === 'done' ? '#4ade80' : nState.classify === 'active' ? DOC.tagColor : '#9ca3af'
              }}>Classify</span>
            </div>

            {/* EXTRACT node */}
            <div className="pa-node" style={{ left: '32%', top: '164px' }}>
              {nState.extract === 'active' && <div className="pa-ring" style={{ borderColor: DOC.tagColor }}/>}
              <NodeBox state={nState.extract} color={DOC.tagColor} num="2"/>
              <span className="pa-nlbl" style={{
                color: nState.extract === 'done' ? '#4ade80' : nState.extract === 'active' ? DOC.tagColor : '#9ca3af'
              }}>Extract</span>
            </div>

            {/* ANNOTATOR node */}
            <div className="pa-node" style={{ left: '52%', top: '292px' }}>
              {nState.annotate === 'active' && <div className="pa-ring" style={{ borderColor: '#fbbf24' }}/>}
              <NodeBox state={nState.annotate} color="#fbbf24" icon={
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
              }/>
              <span className="pa-nlbl pa-nlbl--sm" style={{
                color: nState.annotate === 'done' ? '#4ade80' : nState.annotate === 'active' ? '#fbbf24' : '#9ca3af'
              }}>Annotator</span>
            </div>

            {/* QA node */}
            <div className="pa-node" style={{ left: '70%', top: '292px' }}>
              {nState.qa === 'active' && <div className="pa-ring" style={{ borderColor: '#8b5cf6' }}/>}
              <NodeBox state={nState.qa} color="#8b5cf6" icon={
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              }/>
              <span className="pa-nlbl pa-nlbl--sm" style={{
                color: nState.qa === 'done' ? '#4ade80' : nState.qa === 'active' ? '#8b5cf6' : '#9ca3af'
              }}>QA Check</span>
            </div>

            {/* REPORT node */}
            <div className="pa-node" style={{ left: '90%', top: '164px' }}>
              {nState.report === 'active' && <div className="pa-ring" style={{ borderColor: '#d8572a' }}/>}
              <NodeBox state={nState.report} color="#d8572a" icon={
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
              }/>
              <span className="pa-nlbl" style={{
                color: nState.report === 'active' ? '#d8572a' : '#9ca3af'
              }}>Report</span>
            </div>

          </div>{/* end pa-pipeline */}

        </div>

      </div>
    </div>
  );
};

export default PipelineAnimation;
